/**
 * The app-wide React Query retry policy (#2252).
 *
 * One retry for a failure that might pass on a second try: a network error, a
 * 5xx or a 408 timeout. No retry for any 4xx, because the server has answered
 * and will answer the same again.
 *
 * That includes 429 (#2382). The per-user rate-limit window is 15 minutes, so
 * a retry a second later lands in the same exhausted bucket and only spends
 * it further. The 2026-10-08 E2E campaign saw every 429 GET doubled.
 *
 * Why it matters beyond the wasted request: React Query PAUSES a pending retry
 * while the tab is hidden (`focusManager` reads `document.visibilityState`).
 * The query stays `pending` until the tab is shown, so a component that shows
 * a skeleton while pending shows it for as long as the tab stays hidden. On
 * 2026-10-06 the parent Grants page sat on two skeletons in a background tab
 * after one 400 from GET /api/grants, and the production log shows the retry
 * never fired.
 */
export const MAX_RETRIES = 1;

function httpStatusOf(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  const status = httpStatusOf(error);
  if (status === undefined) return true;
  if (status === 408) return true;
  return status < 400 || status >= 500;
}
